import React, { useState } from 'react';
import { ISummaryPageSettings } from '../../../models/summary/ISummaryPageSettings';
import styles from './summary-degree.module.scss';
import { ISummaryData } from '../../../models/summary/ISummaryData';
import { ButtonStyleType } from '../../../constants/button-style-type';
import Button from '../../../components/atoms/button/button';
import TagManager from 'react-gtm-module';
import { CourseType } from '../../../constants/course-type';
import { ISummaryCourse } from '../../../models/summary/ISummaryCourse';
import Link from 'next/link';
import { InfoIcon } from '../../../components/atoms/icons/icons';
import {
  formatSummaryInstanceDate,
  removeSpacefromText,
} from '../../../utils/string-helpers/formatting.helper';
import { ICourseInstance } from '../../../models/course/ICourseData';
import { ApplicationMethod } from '../../../constants/application-method';
import moment from 'moment';
import IconTooltip from '../icon-tooltip/icon-tooltip';
import InfoPopup from '../../atoms/info-popup/info-popup';
import { StudentType } from '../../../constants/student-type';
import colorStyle from '../../../styles/utils/variables.module.scss';
import { ApplicationType } from '../../../constants/application-type';
import IButton from '../../../models/button/IButton';
import ModalBox from '../modal-box/modal-box';

const applicationMethodInfoText = {
  GlobalApplicationForm: 'Apply via the partner portal application form.',
  DirectEntryWaiver: 'Apply via university website and complete DEW form.',
};

type Props = {
  degree: ISummaryData;
  summaryPageSettings: ISummaryPageSettings;
  brandName: string;
  application: string;
  googleTagId?: string;
  courseList?: ISummaryCourse[];
  studentLevel?: StudentType;
};
export default function SummaryDegree({
  degree,
  summaryPageSettings,
  brandName,
  application,
  googleTagId,
  courseList,
  studentLevel,
}: Props) {
  // for redirect modal
  const [showModal, setShowModal] = useState(false);

  let applyURL = null;
  let applyBtnText = `Apply`;

  const pushDataLayer = () => {
    if (googleTagId) {
      TagManager.dataLayer({
        dataLayer: { brandName: brandName, courseName: degree.name, event: 'applyButtonClick' },
      });
    }

    // for redirect modal
    if (application == ApplicationType.UniBrandedB2c) {
      setShowModal(true);
    }
  };

  if (summaryPageSettings.b2cFlag) {
    applyURL = summaryPageSettings.applyURL + `/` + degree.brandId + `/` + degree.id;
  } else {
    if (
      degree.applicationMethods.includes(removeSpacefromText(CourseType.DirectEntryWaiver, 'all'))
    ) {
      applyURL = `${summaryPageSettings.dewApplyURL}?brandId=${degree.brandId}&programType=${degree.level}&programId=${degree.id}`;
      applyBtnText = `Waiver`;
    } else {
      applyURL = summaryPageSettings.applyURL;
    }
  }

  const courseSlug = courseList?.filter((c) => c.programId === degree?.id)[0]?.programSlug;
  const firstInstance = degree.instances?.at(0);
  const buildRequirements = (instance: ICourseInstance) => {
    if (!instance || !instance.requirements) return <></>;

    return (
      <ul>
        {studentLevel === StudentType.Transfer ? (
          <li className='mb-2'>{instance.transferAcademicEntryRequirements}</li>
        ) : (
          <li className='mb-2'>{instance.requirements.academic}</li>
        )}
        <li>{instance.requirements.language}</li>
      </ul>
    );
  };

  const [degreeRequirementsOpened, setDegreeRequirementsOpened] = useState(false);

  const onDegreeRequirementsClick = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setDegreeRequirementsOpened(true);
  }, []);

  if (!degree.instances || degree.instances.length === 0) return <></>;

  const orderedInstances = degree.instances.sort((c, d) => {
    const cDate = moment(c.startDate);
    const dDate = moment(d.startDate);

    if (cDate.isBefore(dDate)) {
      return -1;
    } else if (cDate.isAfter(dDate)) {
      return 1;
    } else {
      return 0;
    }
  });

  const uniqueInstancesByStartDate = [
    ...new Map(orderedInstances.filter(Boolean).map((item) => [item['startDate'], item])).values(),
  ];

  const buttonProps = {
    buttonStyle: ButtonStyleType.Filled,
    buttonSize: summaryPageSettings.buttonSize,
    copyColor: summaryPageSettings.buttonCopyColor,
    backgroundColor: summaryPageSettings.backgroundColor,
    hoverBackgroundColor: summaryPageSettings.hoverBackgroundColor,
    activeBackgroundColor: summaryPageSettings.activeBackgroundColor,
    buttonAriaLabel: `Apply to ${degree?.name}`,
    heapIdentifier: 'degree-apply-cta',
    children: 'Apply',
    buttonID: 'degree-apply-cta',
    onClick: pushDataLayer,
  };

  const modalFooterButton: IButton = {
    ...buttonProps,
    buttonURL: applyURL,
    buttonRole: 'link',
    buttonID: 'degree-modalbox-apply-cta',
    buttonName: 'degree-modalbox-apply-cta',
    onClick: () => setShowModal(false),
    children: summaryPageSettings?.redirectModal?.buttonText,
  };

  return (
    <>
      {application == ApplicationType.UniBrandedB2c && (
        <ModalBox
          isOpen={showModal}
          title={summaryPageSettings?.redirectModal?.title}
          onClose={() => setShowModal(false)}
          footerButton={modalFooterButton}
          autoClickAfterDelay
          modalSize='lg'
          modalVerticallyCenter
        >
          {summaryPageSettings?.redirectModal?.message}
        </ModalBox>
      )}
      <article className={`${styles[`summary-degree`]} pt-4`}>
        <div className={`${styles[`degree-heading`]}`}>
          {courseSlug ? (
            <h4 className={`${styles[`degree-name`]}`}>
              <Link href={summaryPageSettings.brandURL + '/courses/' + courseSlug}>
                {degree.name}
              </Link>
            </h4>
          ) : (
            <h4 className={`${styles[`degree-name`]}`}>{degree.name}</h4>
          )}
          {degree.cricosCodes && degree.cricosCodes.length && (
            <p className={`${styles[`cricos`]} ${styles[`desktop-only`]}`}>
              <span>Course CRICOS code: </span>
              {degree.cricosCodes[0]}
            </p>
          )}
          <div className={`${styles[`apply`]} ${styles[`desktop-only`]}`}>
            {application == ApplicationType.UniBrandedB2c ? (
              <Button {...buttonProps}>{applyBtnText}</Button>
            ) : (
              <Button {...buttonProps} buttonNewTab='_blank' buttonURL={applyURL} buttonRole='link'>
                {applyBtnText}
              </Button>
            )}
          </div>
        </div>
        {degree.degreeCredits && (
          <div>
            <div className='d-flex'>
              <p className={`${styles[`topper`]}`}>Total Credits</p>
              {studentLevel === StudentType.Transfer && (
                <IconTooltip
                  svgIcon={InfoIcon()}
                  tooltipAccessibilityLabel='Total Credits - additional details'
                  heading='Total Credits'
                  svgIconColor={colorStyle.blue100}
                  activeSVGIconColor={colorStyle.blue200}
                  helperClass={`${styles[`info-icon`]} ms-1`}
                >
                  This is the total number of credits required to complete the program, the number
                  you will earn depends on how many credits you are transferring.
                </IconTooltip>
              )}
            </div>
            <p>{degree.degreeCredits}</p>
          </div>
        )}
        {degree.isStem !== null && degree.isStem !== undefined && (
          <div>
            <p className={`${styles[`topper`]}`}>STEM</p>
            <p>{degree.isStem ? 'YES' : 'NO'}</p>
          </div>
        )}
        {application === `PartnerPortal` && degree.applicationMethods?.length && (
          <div>
            <div className='d-flex'>
              <p className={`${styles[`topper`]}`}>Application Method</p>
              <IconTooltip
                svgIcon={InfoIcon()}
                tooltipAccessibilityLabel='Application Method - additional details'
                heading='Application Method'
                svgIconColor={colorStyle.blue}
                activeSVGIconColor={colorStyle.blue200}
                helperClass={`${styles[`info-icon`]} ms-1`}
              >
                {applicationMethodInfoText[degree.applicationMethods[0]]}
              </IconTooltip>
            </div>
            <ul>
              {degree.applicationMethods
                .map((am) => ApplicationMethod[am])
                .map((method, idx) => (
                  <li key={idx}>{method}</li>
                ))}
            </ul>
          </div>
        )}
        {studentLevel !== StudentType.Transfer && (
          <div>
            <p className={`${styles[`topper`]}`}>Estimated Duration</p>
            <p>{degree.instances[0]?.programLength}</p>
          </div>
        )}
        <div className={`${styles[`next-start`]}`}>
          <p className={`${styles[`topper`]}`}>Next Start Dates</p>
          <ul>
            {formatSummaryInstanceDate(
              uniqueInstancesByStartDate.map((instance) => instance.startDate),
            ).map((date, idx) => (
              <li key={idx}>{date}</li>
            ))}
          </ul>
        </div>
        <div className={`${styles[`empty-grid-padding`]}`}></div>
        <div className={`${styles[`app-deadline`]}`}>
          <p className={`${styles[`topper`]}`}>
            {studentLevel === StudentType.Transfer ? 'Transfer Close Date' : 'App Deadline'}
          </p>
          <ul>
            {formatSummaryInstanceDate(
              uniqueInstancesByStartDate.map((instance) => {
                const closeDate =
                  studentLevel === StudentType.Transfer
                    ? instance.transferApplicationCloseDate
                    : instance.applicationCloseDate;
                return instance.startDate === closeDate ? 'TBC' : closeDate;
              }),
            ).map((date, idx) => (
              <li key={idx}>{date}</li>
            ))}
          </ul>
        </div>
        <div className={`${styles[`requirements`]}`}>
          <button
            className={`${styles['btn-link']} ${degreeRequirementsOpened ? styles.active : null}`}
            onClick={onDegreeRequirementsClick}
            aria-haspopup='dialog'
          >
            Degree Specific Requirements
          </button>
          <div aria-live='polite' aria-atomic>
            {degreeRequirementsOpened && (
              <InfoPopup
                heading='Degree Specific Requirements'
                onClose={() => setDegreeRequirementsOpened(false)}
                cssClass={styles['requirements-tooltip-content']}
              >
                {buildRequirements(firstInstance)}
              </InfoPopup>
            )}
          </div>
        </div>
        <div className={`${styles[`degree-footer`]}`}>
          {/* <a className={`${styles[`degree-link`]}`} rel="noopener" href='#'>
            <ExternalLinkIcon fill="#336B9B" /> Visit University Department
          </a> */}
          {degree.cricosCodes && degree.cricosCodes.length && (
            <p className={`${styles[`cricos`]} ${styles[`mobile-only`]}`}>
              <span>Course CRICOS code: </span>
              {degree.cricosCodes[0]}
            </p>
          )}
          <div className={`${styles[`apply`]} ${styles[`mobile-only`]}`}>
            {application == ApplicationType.UniBrandedB2c ? (
              <Button
                buttonStyle={ButtonStyleType.Filled}
                buttonID={`applyDegreeBtn`}
                buttonSize={summaryPageSettings.buttonSize}
                copyColor={summaryPageSettings.buttonCopyColor}
                backgroundColor={summaryPageSettings.backgroundColor}
                hoverBackgroundColor={summaryPageSettings.hoverBackgroundColor}
                activeBackgroundColor={summaryPageSettings.activeBackgroundColor}
                buttonAriaLabel={degree?.name}
                onClick={pushDataLayer}
              >
                {applyBtnText}
              </Button>
            ) : (
              <Button
                buttonStyle={ButtonStyleType.Filled}
                buttonNewTab='_blank'
                buttonID={`applyDegreeBtn`}
                buttonURL={applyURL}
                buttonRole='link'
                buttonSize={summaryPageSettings.buttonSize}
                copyColor={summaryPageSettings.buttonCopyColor}
                backgroundColor={summaryPageSettings.backgroundColor}
                hoverBackgroundColor={summaryPageSettings.hoverBackgroundColor}
                activeBackgroundColor={summaryPageSettings.activeBackgroundColor}
                buttonAriaLabel={degree?.name}
                onClick={pushDataLayer}
              >
                {applyBtnText}
              </Button>
            )}
          </div>
        </div>
      </article>
    </>
  );
}
